import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "header-block h1-header justify-space-between" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h1", null, " Team Evaluation — Peer Feedback Review ", -1)
const _hoisted_3 = {
  key: 1,
  class: "btn btn-burg disabled-generate category-tooltip",
  type: "button",
  name: "button",
  "aria-disabled": "true"
}
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", { class: "tool-tip" }, " This associated Reflection Lesson must release before you can release feedback. ", -1)
const _hoisted_5 = { class: "header-block h2-header justify-start" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "label" }, "Survey Details:", -1)
const _hoisted_7 = ["href"]
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("i", null, "Review the open-ended comments that peers have consented to share with their team members to ensure they are safe to share.")
], -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { class: "info-card empty beige" }, [
  /*#__PURE__*/_createElementVNode("p", null, "There are no open-ended comments that need to be reviewed.")
], -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("i", null, [
    /*#__PURE__*/_createTextVNode("The following open-ended comments have been "),
    /*#__PURE__*/_createElementVNode("b", null, "accepted"),
    /*#__PURE__*/_createTextVNode(", but have yet to be released and shared with students.")
  ])
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("div", { class: "info-card empty beige" }, [
  /*#__PURE__*/_createElementVNode("p", null, "There are no open-ended comments that have been accepted.")
], -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("i", null, [
    /*#__PURE__*/_createTextVNode("The following open-ended comments have been "),
    /*#__PURE__*/_createElementVNode("b", null, "rejected"),
    /*#__PURE__*/_createTextVNode(" and won’t be shared with students.")
  ])
], -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("div", { class: "info-card empty beige" }, [
  /*#__PURE__*/_createElementVNode("p", null, "There are no open-ended comments that have been rejected.")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("i", null, [
    /*#__PURE__*/_createTextVNode("The following open-ended comments have been "),
    /*#__PURE__*/_createElementVNode("b", null, "released"),
    /*#__PURE__*/_createTextVNode(" and shared with students.")
  ])
], -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("div", { class: "info-card empty beige" }, [
  /*#__PURE__*/_createElementVNode("p", null, "There are no open-ended comments that have been accepted released.")
], -1)

import { ref, onMounted, computed } from "vue";
import { DateTime } from "luxon";
import { TANDEM_DATE_SHORT } from "@/core";
import {
  getTeamFeedback,
  Team,
  RatingOutcome,
  toTeamsWithOutcome,
  releaseSharedTeamMemberRatings,
  ReceivedFeedback,
  TeamFeedback,
} from "@/services/surveyFeedbackService";
import TabbedPageLayout from "@/layouts/TabbedPageLayout.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import FeedbackReleaseModal from "@/components/FeedbackReleaseModal.vue";
import FeedbackReviewTab from "@/components/FeedbackReviewTab.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'FeedbackReviewer',
  props: {
    cohortId: {},
    cohortSurveyId: {},
    surveyTitle: {},
    surveyUrl: {},
    publishDateIsoString: {},
    dueDateIsoString: {},
    canRelease: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;



const teams = ref<Team[]>([]);
const loading = ref<boolean>(true);
const showModal = ref<boolean>(false);

const acceptedTeamFeedback = computed(() =>
  toTeamsWithOutcome(
    teams.value,
    (feedback: ReceivedFeedback) =>
      feedback.sharedRating.outcome === RatingOutcome.Accepted
  )
);
const releasedTeamFeedback = computed(() =>
  toTeamsWithOutcome(
    teams.value,
    (feedback: ReceivedFeedback) =>
      feedback.sharedRating.outcome === RatingOutcome.Released
  )
);
const rejectedTeamFeedback = computed(() =>
  toTeamsWithOutcome(
    teams.value,
    (feedback: ReceivedFeedback) =>
      feedback.sharedRating.outcome === RatingOutcome.Rejected
  )
);
const reviewTeamFeedback = computed(() =>
  toTeamsWithOutcome(
    teams.value,
    (feedback: ReceivedFeedback) =>
      feedback.sharedRating.outcome === RatingOutcome.Review
  )
);

const feedbackCount = (teamFeedback: TeamFeedback[]): number =>
  teamFeedback
    .flatMap((team) =>
      team.members.flatMap((member) =>
        member.questions.map((question) => question.responses.length)
      )
    )
    .reduce((a, b) => a + b, 0);

const tabTitle = (title: string, teamFeedback: TeamFeedback[]) => {
  const count = feedbackCount(teamFeedback);
  return count === 0 ? title : `${title} (${count})`;
};

const tabs = computed(() => [
  {
    title: tabTitle("Review Needed", reviewTeamFeedback.value),
    key: "review",
  },
  {
    title: tabTitle("Accepted", acceptedTeamFeedback.value),
    key: "accepted",
  },
  {
    title: tabTitle("Rejected", rejectedTeamFeedback.value),
    key: "rejected",
  },
  {
    title: tabTitle("Released", releasedTeamFeedback.value),
    key: "released",
  },
]);

const formatDate = (isoDateString: string) =>
  DateTime.fromISO(isoDateString).toFormat(TANDEM_DATE_SHORT);

const fetchFeedback = async () => {
  teams.value = await getTeamFeedback(props.cohortId, props.cohortSurveyId);
};

const finalizeReview = async () => {
  await releaseSharedTeamMemberRatings(props.cohortId, props.cohortSurveyId);
  await fetchFeedback();
};

const handleRelease = async () => {
  await finalizeReview();
  showModal.value = false;
};

onMounted(async () => {
  loading.value = true;
  await fetchFeedback();
  loading.value = false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(TabbedPageLayout, { tabs: tabs.value }, _createSlots({
      "general-block": _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _hoisted_2,
          (_ctx.canRelease)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "btn btn-burg",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showModal.value = true))
              }, " Release Feedback "))
            : (_openBlock(), _createElementBlock("button", _hoisted_3, [
                _createTextVNode(" Release Feedback "),
                _hoisted_4
              ]))
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("h2", null, [
            _hoisted_6,
            _createTextVNode(" " + _toDisplayString(_ctx.surveyTitle) + " - " + _toDisplayString(formatDate(_ctx.publishDateIsoString)) + " — " + _toDisplayString(formatDate(_ctx.dueDateIsoString)), 1)
          ]),
          _createElementVNode("a", {
            class: "btn",
            href: _ctx.surveyUrl
          }, " View Details ", 8, _hoisted_7)
        ])
      ]),
      review: _withCtx(() => [
        _createVNode(FeedbackReviewTab, {
          "cohort-id": _ctx.cohortId,
          "cohort-survey-id": _ctx.cohortSurveyId,
          teams: reviewTeamFeedback.value,
          "accept-selected": true,
          "reject-selected": true,
          onRatingsUpdated: fetchFeedback,
          class: "tab-group review-tab"
        }, {
          info: _withCtx(() => [
            _hoisted_8
          ]),
          empty: _withCtx(() => [
            _hoisted_9
          ]),
          _: 1
        }, 8, ["cohort-id", "cohort-survey-id", "teams"])
      ]),
      accepted: _withCtx(() => [
        _createVNode(FeedbackReviewTab, {
          "cohort-id": _ctx.cohortId,
          "cohort-survey-id": _ctx.cohortSurveyId,
          teams: acceptedTeamFeedback.value,
          "accept-selected": false,
          "reject-selected": true,
          onRatingsUpdated: fetchFeedback,
          class: "tab-group accepted-tab"
        }, {
          info: _withCtx(() => [
            _hoisted_10
          ]),
          empty: _withCtx(() => [
            _hoisted_11
          ]),
          _: 1
        }, 8, ["cohort-id", "cohort-survey-id", "teams"])
      ]),
      rejected: _withCtx(() => [
        _createVNode(FeedbackReviewTab, {
          "cohort-id": _ctx.cohortId,
          "cohort-survey-id": _ctx.cohortSurveyId,
          teams: rejectedTeamFeedback.value,
          "accept-selected": true,
          "reject-selected": false,
          onRatingsUpdated: fetchFeedback,
          class: "tab-group rejected-tab"
        }, {
          info: _withCtx(() => [
            _hoisted_12
          ]),
          empty: _withCtx(() => [
            _hoisted_13
          ]),
          _: 1
        }, 8, ["cohort-id", "cohort-survey-id", "teams"])
      ]),
      released: _withCtx(() => [
        _createVNode(FeedbackReviewTab, {
          "cohort-id": _ctx.cohortId,
          "cohort-survey-id": _ctx.cohortSurveyId,
          teams: releasedTeamFeedback.value,
          "accept-selected": false,
          "reject-selected": false,
          class: "tab-group released-tab"
        }, {
          info: _withCtx(() => [
            _hoisted_14
          ]),
          empty: _withCtx(() => [
            _hoisted_15
          ]),
          _: 1
        }, 8, ["cohort-id", "cohort-survey-id", "teams"])
      ]),
      _: 2
    }, [
      (loading.value)
        ? {
            name: "tab-content-container",
            fn: _withCtx(() => [
              _createVNode(LoadingIndicator)
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["tabs"]),
    _createVNode(FeedbackReleaseModal, {
      "team-count": acceptedTeamFeedback.value.length,
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      onRelease: handleRelease
    }, null, 8, ["team-count", "modelValue"])
  ], 64))
}
}

})